import { useMemo } from 'react';
import { ISelectOption } from '@constants/entities/ui';
import { useGetQnousRefsQuery } from '@store/services/qnous-ref';
import { IQnousRef } from '@store/services/qnous-ref/types';

const transformRefsToOptions = (
  data: undefined | IQnousRef[],
): ISelectOption[] =>
  data
    ?.filter(({ hidden }) => !hidden)
    .map(({ id, name }) => ({ value: id, label: name })) ?? [];

/**
 * The five reference catalogues as dropdown options.
 *
 * Built once per fetch rather than per render: all five reference inputs call
 * this hook, and a fresh array each time would also change the `options` prop
 * identity, making MUI Autocomplete re-filter and re-render its whole list.
 */
const useQnousRefOptions = (): [
  {
    qnousOptions: ISelectOption[];
    nistOptions: ISelectOption[];
    isoOptions: ISelectOption[];
    ismOptions: ISelectOption[];
    cisOptions: ISelectOption[];
  },
  boolean,
] => {
  const { data, isLoading, isFetching } = useGetQnousRefsQuery();

  const options = useMemo(
    () => ({
      qnousOptions: transformRefsToOptions(data?.qnous_ref),
      nistOptions: transformRefsToOptions(data?.nist),
      isoOptions: transformRefsToOptions(data?.iso),
      ismOptions: transformRefsToOptions(data?.ism),
      cisOptions: transformRefsToOptions(data?.cis),
    }),
    [data],
  );

  return [options, isLoading || isFetching];
};

export default useQnousRefOptions;
